import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { useMemo } from 'react';

export type Selector<State, Result> = (state: State) => Result;

export type SelectorCache<State = any> = Map<
  () => Selector<State, any>,
  Selector<State, any>
>;

/**
 * Create a selector that is cached in scope of the object it is called on.
 *
 * This is a robust solution to reselect's global memoization nature issue:
 * https://github.com/reduxjs/reselect#sharing-selectors-with-props-across-multiple-component-instances
 * This function handles the issue of instantiating the selectors for each component instance (scope),
 * so we don't have to do it manually like in that article.
 *
 * State object used for scoped selectors must contain a selector cache (Map instace).
 * Keep single cache object where you want a single memoization scope (e.g. component instance).
 * If you have multiple component instances that use the same scoped selectors on page,
 * they will be memoized separately in each cache object and won't mutually flush each other's
 * memoized value.
 *
 * For example usage with React, see 'works as expected in React' test case.
 *
 * Scoped selectors are also able to work with context selectors, e.g.
 * ```
 * const selectFoo = createScopedSelector(() => createSelector(...));
 * useContextSelector(myComponentContext, selectFoo);
 * ```
 * The context has just to provide the selectorCache object.
 */
export const createScopedSelector =
  <State extends { selectorCache: SelectorCache<State> }, Result>(
    selectorFactory: () => Selector<State, Result>,
  ): Selector<State, Result> =>
  (state) =>
    plainMap.getOrCreate(selectorFactory, selectorFactory)(state.selectorCache)(
      state,
    );

/**
 * Provide a memoized selector cache object for a component
 */
export const useSelectorCache = <State,>(): SelectorCache<State> =>
  useMemo(() => new Map(), []);
